import React, { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { Loader2, Image as ImageIcon, Upload } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * The "+ Upload" attachment control shared by task creation and task detail.
 *
 * Desktop: a single tap opens the OS file picker (any file type).
 * Mobile: a tap opens an iOS-style bottom action sheet so the user can pick the
 * source -
 *   • "Photo or video" → an image/video input, which the OS opens straight to
 *     the photo gallery (the part that was missing before), and
 *   • "File" → the generic any-type file picker.
 *
 * The sheet is built on the Radix Dialog primitive (rather than a plain portaled
 * div) so it layers correctly above the surrounding task modal, stays
 * interactive, traps focus and locks background scrolling.
 *
 * Both inputs funnel into the same `onChange`, so the parent's existing upload
 * handler (which reads `e.target.files`) works unchanged.
 */
const AttachmentUploadButton = ({ onChange, uploading = false, count = 0, disabled = false }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const openFiles = () => { setSheetOpen(false); fileInputRef.current?.click(); };
  const openGallery = () => { setSheetOpen(false); galleryInputRef.current?.click(); };

  const handleClick = () => {
    if (isMobile) setSheetOpen(true);
    else openFiles();
  };

  const optionClass = 'w-full flex items-center gap-3 px-5 py-4 text-[15px] text-foreground active:bg-accent text-left';

  return (
    <div className="inline-flex">
      <button
        type="button"
        onClick={handleClick}
        disabled={uploading || disabled}
        className={`flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground/80 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {uploading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <>
            <span>+ {t('tasks.upload', 'Upload')}</span>
            {count > 0 && <span className="text-muted-foreground/70 ml-1">({count})</span>}
          </>
        )}
      </button>

      {isMobile && (
        <Dialog.Root open={sheetOpen} onOpenChange={setSheetOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[80] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
            <Dialog.Content
              aria-describedby={undefined}
              className="fixed inset-x-0 bottom-0 z-[80] p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=closed]:duration-200"
            >
              <Dialog.Title className="sr-only">{t('tasks.attachments', 'Attachments')}</Dialog.Title>
              <div className="rounded-2xl bg-card border border-border overflow-hidden mb-2">
                <button type="button" onClick={openGallery} className={`${optionClass} border-b border-border`}>
                  <ImageIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  {t('tasks.upload_gallery', 'Photo or video')}
                </button>
                <button type="button" onClick={openFiles} className={optionClass}>
                  <Upload className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  {t('tasks.upload_file_option', 'File')}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="w-full rounded-2xl bg-card border border-border py-3.5 text-[15px] font-semibold text-foreground active:bg-accent"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {/* Generic any-type picker (desktop default + mobile "File"). */}
      <input
        ref={fileInputRef}
        type="file"
        accept="*/*"
        multiple
        className="hidden"
        onChange={onChange}
      />
      {/* Gallery picker (mobile "Photo or video"). accept opens the OS straight
          to photos/videos. */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={onChange}
      />
    </div>
  );
};

export default AttachmentUploadButton;
